/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { memo } from 'preact/compat';
import { Fragment, FunctionComponent } from 'preact';
import { useAddresses } from '@/account/hooks/containers/useAddresses';
import { AddressForm } from '@/account/containers/AddressForm';
import { Card, Header } from '@adobe-commerce/elsie/components';
import {
  AddressActions,
  AddressCard,
  AddressModal,
  CardLoader,
  EmptyList,
} from '@/account/components';
import { AddressesWrapperProps } from '@/account/types';
import '@/account/components/AddressesWrapper/AddressesWrapper.css';
import sortingAddressesList from '@/account/lib/sortingAddressesList';

export const AddressesWrapper: FunctionComponent<AddressesWrapperProps> = memo(
  ({
    minifiedViewKey,
    hideActionFormButtons = false,
    inputName,
    slots,
    title = '',
    addressFormTitle = '',
    defaultSelectAddressId = '',
    showFormLoader = false,
    onAddressData,
    forwardFormRef,
    className,
    showSaveCheckBox = false,
    saveCheckBoxValue = false,
    selectShipping = false,
    selectBilling = false,
    selectable = false,
    withHeader = true,
    minifiedView = false,
    withActionsInMinifiedView = false,
    withActionsInFullSizeView = true,
    inputsDefaultValueSet,
    showShippingCheckBox = true,
    showBillingCheckBox = true,
    shippingCheckBoxValue = true,
    billingCheckBoxValue = true,
    routeAddressesPage,
    onSubmit,
    onSuccess,
    onError,
  }) => {
    const translations = useText({
      containerTitle: `Account.${minifiedViewKey}.Addresses.containerTitle`,
      differentAddressFormTitle: `Account.${minifiedViewKey}.Addresses.differentAddressFormTitle`,
      editAddressFormTitle: `Account.${minifiedViewKey}.Addresses.editAddressFormTitle`,
      viewAllAddressesButton: `Account.${minifiedViewKey}.Addresses.viewAllAddressesButton`,
      newAddressFormTitle: `Account.${minifiedViewKey}.Addresses.newAddressFormTitle`,
      ariaLabelAddressPicker: `Account.${minifiedViewKey}.Addresses.ariaLabelAddressPicker`,
    });

    const {
      keysSortOrder,
      submitLoading,
      isModalRendered,
      isFormRendered,
      loading,
      addNewAddress,
      addressesList,
      addressId,
      handleRenderForm,
      handleRenderModal,
      removeAddress,
      onCloseBtnClick,
      handleOnSuccess,
      setEditingAddressId,
      closeNewAddressForm,
      redirectToAddressesRoute,
      handleSelectAddressOption,
      selectedAddressOption,
    } = useAddresses({
      defaultSelectAddressId,
      minifiedView,
      routeAddressesPage,
      onSuccess,
      onAddressData,
      selectShipping,
      selectBilling,
    });

    const headerTitle = title || translations.containerTitle;

    let addressesComponent = null;

    if (selectable) {
      const sortedAddressesList =
        sortingAddressesList(addressesList, selectShipping, selectBilling) ||
        [];

      let addressesFooterSection;

      if (addNewAddress) {
        addressesFooterSection = (
          <div
            className={classes([
              'account-addresses-form__footer__wrapper',
              ['account-addresses-form__footer__wrapper-show', addNewAddress],
            ])}
          >
            <AddressForm
              slots={slots}
              hideActionFormButtons={hideActionFormButtons}
              formName={inputName}
              showFormLoader={showFormLoader}
              isOpen={addNewAddress}
              forwardFormRef={forwardFormRef}
              showSaveCheckBox={showSaveCheckBox}
              saveCheckBoxValue={saveCheckBoxValue}
              shippingCheckBoxValue={shippingCheckBoxValue}
              billingCheckBoxValue={billingCheckBoxValue}
              addressesFormTitle={
                addressFormTitle || translations.differentAddressFormTitle
              }
              inputsDefaultValueSet={inputsDefaultValueSet}
              showShippingCheckBox={showShippingCheckBox}
              showBillingCheckBox={showBillingCheckBox}
              onCloseBtnClick={closeNewAddressForm}
              onChange={onAddressData}
              onError={onError}
              onSubmit={onSubmit}
              onSuccess={handleOnSuccess}
            />
          </div>
        );
      } else if (sortedAddressesList.length) {
        addressesFooterSection = (
          <AddressActions
            selectable={selectable}
            minifiedView={minifiedView}
            addNewAddress={addNewAddress}
            routeAddressesPage={redirectToAddressesRoute}
          />
        );
      } else {
        addressesFooterSection = null;
      }

      const generalLabelId = `generalLabel_${headerTitle.replace(/\s+/g, '')}`;

      addressesComponent = (
        <div className={'account-addresses-wrapper--select-view'}>
          <span id={generalLabelId} style="display: none;">
            {headerTitle}
          </span>
          {sortedAddressesList.map((item, index) => {
            const valueForSelection = item.id?.toString() || item.uid;
            const inputId = `${inputName}_${valueForSelection}`;
            const labelId = `${inputId}_label`;

            return (
              <Fragment key={valueForSelection}>
                <input
                  data-testid={`radio-${index + 1}`}
                  type="radio"
                  name={inputName}
                  id={inputId}
                  value={valueForSelection}
                  checked={selectedAddressOption === valueForSelection}
                  onChange={(event) => handleSelectAddressOption(event, item)}
                  aria-labelledby={`${generalLabelId} ${labelId}`}
                />
                <label
                  id={labelId}
                  htmlFor={inputId}
                  className={'account-addresses-wrapper__label'}
                >
                  <AddressCard
                    slots={slots}
                    selectable={selectable}
                    selectShipping={selectShipping}
                    selectBilling={selectBilling}
                    minifiedView={minifiedView}
                    addressData={item}
                    keysSortOrder={keysSortOrder}
                    loading={loading}
                  />
                </label>
              </Fragment>
            );
          })}

          {addressesFooterSection && (
            <div className="account-addresses-wrapper__footer-section">
              <input
                aria-label={`${headerTitle} ${translations.ariaLabelAddressPicker}`}
                data-testid={`radio-0`}
                type="radio"
                name={inputName}
                id={`${inputName}_addressActions`}
                value={'0'}
                checked={selectedAddressOption === '0'}
                onChange={(event) => handleSelectAddressOption(event, {})}
                tabindex={addressesFooterSection ? 0 : -1}
              />
              <label
                htmlFor={`${inputName}_addressActions`}
                className={'account-addresses-wrapper__label'}
              >
                {addressesFooterSection}
              </label>
            </div>
          )}
        </div>
      );
    } else {
      addressesComponent = (
        <>
          {addressesList.map((item) => (
            <Fragment key={item.id}>
              {addressId === item.id && isFormRendered ? (
                <Card variant="secondary" style={{ marginBottom: 20 }}>
                  <AddressForm
                    slots={slots}
                    isOpen={addressId === item.id && isFormRendered}
                    addressFormId={addressId}
                    inputsDefaultValueSet={item}
                    addressesFormTitle={translations.editAddressFormTitle}
                    showShippingCheckBox={showShippingCheckBox}
                    showBillingCheckBox={showBillingCheckBox}
                    shippingCheckBoxValue={shippingCheckBoxValue}
                    billingCheckBoxValue={billingCheckBoxValue}
                    forwardFormRef={forwardFormRef}
                    onCloseBtnClick={onCloseBtnClick}
                    onChange={onAddressData}
                    onError={onError}
                    onSubmit={onSubmit}
                    onSuccess={handleOnSuccess}
                  />
                </Card>
              ) : (
                <AddressCard
                  slots={slots}
                  key={item.id}
                  minifiedView={minifiedView}
                  addressData={item}
                  keysSortOrder={keysSortOrder}
                  loading={loading}
                  setAddressId={setEditingAddressId}
                  handleRenderModal={
                    (minifiedView && withActionsInMinifiedView) ||
                    (!minifiedView && withActionsInFullSizeView)
                      ? handleRenderModal
                      : undefined
                  }
                  handleRenderForm={
                    (minifiedView && withActionsInMinifiedView) ||
                    (!minifiedView && withActionsInFullSizeView)
                      ? handleRenderForm
                      : undefined
                  }
                />
              )}
            </Fragment>
          ))}

          <div className="account-addresses__footer">
            {addNewAddress ? (
              <Card variant="secondary">
                <AddressForm
                  slots={slots}
                  isOpen={addNewAddress}
                  addressesFormTitle={translations.newAddressFormTitle}
                  inputsDefaultValueSet={inputsDefaultValueSet}
                  showShippingCheckBox={!!addressesList?.length}
                  showBillingCheckBox={!!addressesList?.length}
                  shippingCheckBoxValue={shippingCheckBoxValue}
                  billingCheckBoxValue={billingCheckBoxValue}
                  forwardFormRef={forwardFormRef}
                  onChange={onAddressData}
                  onCloseBtnClick={closeNewAddressForm}
                  onError={onError}
                  onSubmit={onSubmit}
                  onSuccess={handleOnSuccess}
                />
              </Card>
            ) : (
              <AddressActions
                minifiedView={minifiedView}
                addNewAddress={addNewAddress}
                routeAddressesPage={redirectToAddressesRoute}
              />
            )}
          </div>
        </>
      );
    }

    return (
      <div>
        {withHeader ? (
          <Header
            title={headerTitle}
            divider={!minifiedView}
            className={minifiedView ? 'account-addresses-header' : ''}
          />
        ) : null}
        <div
          className={classes(['account-addresses-wrapper', className])}
          data-testid="addressesIdWrapper"
        >
          <AddressModal
            minifiedView={minifiedView}
            addressData={addressesList?.find((el) => el.id === addressId)}
            keysSortOrder={keysSortOrder}
            submitLoading={submitLoading}
            open={isModalRendered}
            closeModal={onCloseBtnClick}
            onRemoveAddress={removeAddress}
          />
          {loading ? (
            <CardLoader testId={'addressSkeletonLoader'} withCard={false} />
          ) : !selectable ? (
            <EmptyList
              isEmpty={!addressesList?.length}
              typeList="address"
              minifiedView={minifiedView}
            />
          ) : (
            <AddressForm
              slots={slots}
              hideActionFormButtons={hideActionFormButtons}
              formName={inputName}
              isOpen={!addressesList?.length}
              forwardFormRef={forwardFormRef}
              showSaveCheckBox={showSaveCheckBox}
              saveCheckBoxValue={saveCheckBoxValue}
              shippingCheckBoxValue={shippingCheckBoxValue}
              billingCheckBoxValue={billingCheckBoxValue}
              inputsDefaultValueSet={inputsDefaultValueSet}
              showShippingCheckBox={showShippingCheckBox}
              showBillingCheckBox={showBillingCheckBox}
              onChange={onAddressData}
              onCloseBtnClick={closeNewAddressForm}
              onError={onError}
              onSubmit={onSubmit}
              onSuccess={handleOnSuccess}
            />
          )}
          {addressesComponent}
        </div>
      </div>
    );
  }
);
